import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { http, apiError } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { useAuth } from "../../lib/auth";
import { DigestPrefs } from "./DigestPrefs";

const COMPANY = [["company_name", "Company name"], ["owner_name", "Owner name"], ["owner_email", "Owner email"], ["manager_name", "Manager name"], ["manager_email", "Manager email"],
  ["info_email", "General inquiries email (receives contact & review alerts)"], ["sales_email", "Sales email (receives asset & sourcing alerts)"],
  ["address_line1", "Address line 1"], ["address_line2", "City, State ZIP"], ["phone", "Phone"]];
const CONTENT = [["hero_eyebrow", "Homepage eyebrow", 1], ["hero_title", "Homepage headline (one line per row)", 3], ["hero_subtitle", "Homepage intro", 3],
  ["mission", "Company mission", 3], ["sourcing_message", "Sourcing message (shown when no listings are available)", 3], ["reviews_placeholder", "Reviews placeholder (shown when no reviews are published)", 2]];

const ChangePassword = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/change-password", f);
      toast.success("Password changed");
      setF({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border hairline p-6 bg-[var(--ink-2)] space-y-4" data-testid="change-password-form">
      <p className="eyebrow">Change password</p>
      <input type="password" required placeholder="Current password" className="field" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="current-password-input" />
      <input type="password" required minLength={8} placeholder="New password (min 8)" className="field" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password-input" />
      <button className="btn-gold" disabled={busy} data-testid="change-password-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password</button>
    </form>
  );
};

export default function SettingsAdmin() {
  const current = useSettings();
  const qc = useQueryClient();
  const [s, setS] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setS(current);
  }, [current]);
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries([...COMPANY, ...CONTENT].map(([k]) => [k, s[k] || ""]));
      await http.put("/admin/settings", body);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <AdminTitle title="Settings" sub={isOwner ? "Company information and website content." : "Your account. Company and website settings are managed by the owner."} />
      <div className="grid xl:grid-cols-3 gap-8">
        {isOwner ? <form onSubmit={save} className="xl:col-span-2 space-y-8">
          <section className="border hairline p-6 bg-[var(--ink-2)] grid sm:grid-cols-2 gap-5">
            <p className="eyebrow sm:col-span-2">Company information</p>
            {COMPANY.map(([k, l]) => (
              <div key={k}><label className="label">{l}</label><input required className="field" value={s[k] || ""} onChange={set(k)} data-testid={`settings-${k}-input`} /></div>
            ))}
          </section>
          <section className="border hairline p-6 bg-[var(--ink-2)] space-y-5">
            <p className="eyebrow">Website content</p>
            {CONTENT.map(([k, l, rows]) => (
              <div key={k}><label className="label">{l}</label>
                {rows > 1 ? <textarea rows={rows} className="field" value={s[k] || ""} onChange={set(k)} data-testid={`settings-${k}-input`} /> : <input className="field" value={s[k] || ""} onChange={set(k)} data-testid={`settings-${k}-input`} />}
              </div>
            ))}
          </section>
          <button className="btn-gold" disabled={busy} data-testid="settings-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save settings</button>
        </form> : <div className="xl:col-span-2 border hairline p-6 bg-[var(--ink-2)] text-sm text-slate-300" data-testid="settings-manager-note">Signed in as {user?.name} ({user?.email}). Contact the owner to change company information or website content.</div>}
        <aside className="space-y-8"><DigestPrefs /><ChangePassword /></aside>
      </div>
    </div>
  );
}
